import cn from 'classnames'
import PropTypes from 'prop-types'
import React, { useState, useRef, useEffect } from 'react'
import { isEmpty, intersection } from 'lodash'

import { ReactComponent as IconClear } from 'assets/icons/close.svg'
import { ReactComponent as IconClose } from 'assets/icons/closeIcon.svg'
import { ReactComponent as IconChevron } from 'assets/icons/IconChevron.svg'

const Autocomplete = React.memo(
  ({
    data,
    handleValueChange,
    isMulti,
    showClearButton,
    showValidationErrors,
    value,
    label,
  }) => {
    const searchInputRef = useRef(null)
    const autocompleteRef = useRef(null)
    const [isAutocompleteOpen, setIsAutocompleteOpen] =
      useState(false)

    const singleOrMultiValue = isMulti ? value : [value]

    const uuidsFromOptions = data.map((item) => item.uuid)

    const multiValue = intersection(
      singleOrMultiValue,
      uuidsFromOptions
    )

    const isLabelExist =
      !isEmpty(multiValue) &&
      !Object.values(multiValue).includes(null) &&
      !Object.values(multiValue).includes('')

    const [selectedIds, setSelectedIds] = useState(
      isLabelExist ? multiValue : []
    )

    useEffect(() => {
      handleValueChange(selectedIds)
      //eslint-disable-next-line
    }, [selectedIds])

    const [searchField, setSearchField] = useState('')
    const ITEMS_COUNT = 1

    const handleClickOnId = (passedId) => {
      const clickedItem = data.find((data) => data.uuid === passedId)

      const itemIsAlreadySelected = selectedIds.find(
        (selectedId) => selectedId === passedId
      )

      if (itemIsAlreadySelected) {
        handleRemoveItem(passedId)
      }

      if (isMulti && !itemIsAlreadySelected) {
        setSelectedIds([...selectedIds, clickedItem.uuid])
      }

      if (!isMulti && !itemIsAlreadySelected) {
        setSelectedIds([clickedItem.uuid])
      }

      setIsAutocompleteOpen(false)
      setSearchField('')
    }

    const handleRemoveItem = (passedId) => {
      if (!isMulti) {
        setSelectedIds([])
      }

      const newSelectedIds = selectedIds.filter(
        (selectedId) => selectedId !== passedId
      )

      setSelectedIds(newSelectedIds)
    }

    const handleChange = (event) => {
      setSearchField(event.target.value)
      setIsAutocompleteOpen(true)
    }

    const filteredData = data.filter((category) => {
      return category.name
        .toLowerCase()
        .includes(searchField.toLowerCase())
    })

    const handleInputClick = (event) => {
      event.preventDefault()

      !isAutocompleteOpen && setIsAutocompleteOpen(true)

      if (!searchField && isAutocompleteOpen) {
        setIsAutocompleteOpen(false)
      }
    }

    const handleSearchSectionClick = () => {
      isAutocompleteOpen
        ? searchInputRef?.current?.blur()
        : searchInputRef?.current?.focus()

      isAutocompleteOpen && setSearchField('')

      setIsAutocompleteOpen(!isAutocompleteOpen)
    }

    const handleClickOutside = (e) => {
      if (autocompleteRef.current.contains(e.target)) {
        return
      }
      setIsAutocompleteOpen(false)
      setSearchField('')
    }

    const isSearchVisible =
      isEmpty(selectedIds) ||
      (!isEmpty(selectedIds) && isAutocompleteOpen)

    useEffect(() => {
      if (isAutocompleteOpen) {
        document.addEventListener('mousedown', handleClickOutside)
      } else {
        document.removeEventListener('mousedown', handleClickOutside)
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }, [isAutocompleteOpen])

    useEffect(() => {
      isAutocompleteOpen && searchInputRef.current.focus()
    }, [isAutocompleteOpen])

    const renderSelectedItems = () => (
      <>
        {selectedIds.length > 0 && (
          <ul className="tags-list">
            {selectedIds.slice(0, ITEMS_COUNT).map((selectedId) => {
              const items = data.filter(
                (item) => item.uuid === selectedId
              )
              return items.map((item) => (
                <li key={item.uuid} className="tags-list__item">
                  <p>{item.name}</p>
                  <button
                    className="tags-list__item__action"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleRemoveItem(item.uuid)
                    }}
                  >
                    <IconClose />
                  </button>
                </li>
              ))
            })}
          </ul>
        )}
        {isSearchVisible && (
          <div className="autocomplete__search">
            <input
              ref={searchInputRef}
              className="autocomplete__input"
              onChange={handleChange}
              onClick={handleInputClick}
              type="text"
              value={searchField}
            />
          </div>
        )}
        <div
          className={cn('autocomplete__label', {
            '-has-value': selectedIds.length,
            '-open': isAutocompleteOpen,
            '-error': showValidationErrors && isEmpty(value),
          })}
        >
          {label}
        </div>
      </>
    )

    return (
      <div
        ref={autocompleteRef}
        onClick={handleSearchSectionClick}
        className={cn('autocomplete', {
          '-has-value': selectedIds.length,
          '-open': isAutocompleteOpen,
          '-empty': !selectedIds.length,
          '-error': showValidationErrors && isEmpty(value),
        })}
        data-testid="autocomplete-wrapper"
        type="button"
      >
        <section className="autocomplete__tags-section">
          {renderSelectedItems()}
          <div
            className={cn('autocomplete__tags-section__actions', {
              '-active': isAutocompleteOpen,
            })}
          >
            {selectedIds.length ? (
              <>
                {showClearButton && (
                  <button
                    className="autocomplete__tags-section__actions__clear-icon"
                    type="button"
                    onClick={(e) => {
                      setSelectedIds([])
                      e.stopPropagation()
                    }}
                    data-testid="clear-button"
                  >
                    <IconClear />
                  </button>
                )}
                {!isAutocompleteOpen &&
                  selectedIds.length > ITEMS_COUNT && (
                    <span data-testid="items-counter">
                      +{selectedIds.length - ITEMS_COUNT}
                    </span>
                  )}
              </>
            ) : null}
            <button
              className={cn('dropdown-icon', {
                '-active': isAutocompleteOpen,
              })}
              type="button"
              data-testid="open-button"
            >
              <IconChevron />
            </button>
          </div>
        </section>
        {isAutocompleteOpen && (
          <ul className="autocomplete__list">
            {isEmpty(filteredData) && (
              <li className="autocomplete__list-item -empty">
                No data available
              </li>
            )}
            {filteredData.map((data) => {
              return (
                <li
                  key={data.uuid}
                  className={cn('autocomplete__list-item', {
                    '-active': selectedIds.find(
                      (selectedId) => selectedId === data.uuid
                    ),
                  })}
                  onClick={() => handleClickOnId(data.uuid)}
                >
                  {data.name}
                </li>
              )
            })}
          </ul>
        )}
      </div>
    )
  }
)

Autocomplete.propTypes = {
  isMulti: PropTypes.bool,
  showClearButton: PropTypes.bool,
  handleValueChange: PropTypes.func,
}

Autocomplete.defaultProps = {
  handleValueChange: () => {},
  data: [],
  isMulti: false,
  showClearButton: false,
  showValidationErrors: false,
}

export default Autocomplete
